<script lang="ts">
    import SidePanel from '@amp/web-app-components/src/components/SidePanel.svelte';
    import NowPlayingQueueList from './NowPlayingQueueList.svelte';
    import UpNextIcon from '@amp/web-app-components/assets/icons/up-next.svg';
    import { createEventDispatcher } from 'svelte';
    import type { ItemMap } from './types';

    export let clearButtonLabel: string;
    export let emptyQueueText: string;
    export let isOpen: boolean = false;
    export let isEmpty: boolean = true;
    export let queueHeading: string;
    export let showClearButton: boolean = false;
    export let unplayedItems: ItemMap[];

    const dispatch = createEventDispatcher();

    let panelId: string;

    function handleKeyboardEvent(e: KeyboardEvent) {
        // Preventing default here ensures the sidebar doesn't
        // scroll as the user navigates up/down with arrow keys
        e.preventDefault();
        dispatch('keyboardEvent', e);
    }
</script>

<button
    on:click={() => dispatch('toggleOpen', !isOpen)}
    class="up-next-queue__button"
    class:active={isOpen}
    aria-expanded={isOpen}
    aria-controls={panelId}
    aria-label={queueHeading}
    data-testid="up-next-button"
>
    <UpNextIcon />
</button>

<SidePanel bind:show={isOpen} bind:panelId {isEmpty}>
    <svelte:fragment slot="header">
        {queueHeading}
    </svelte:fragment>

    <svelte:fragment slot="controls">
        {#if showClearButton}
            <button
                class="side-panel-controls__clear"
                data-testid="up-next-clear"
                on:click={() => dispatch('clearQueue')}
            >
                {clearButtonLabel}
            </button>
        {/if}
    </svelte:fragment>

    <!-- svelte-ignore a11y-no-static-element-interactions -->
    <div
        slot="content"
        class="up-next-queue"
        data-testid="up-next-queue"
        on:keydown={handleKeyboardEvent}
    >
        <NowPlayingQueueList {unplayedItems} on:play on:close />
    </div>

    <div slot="empty" class="up-next-queue__empty" data-testid="up-next-empty">
        {emptyQueueText}
    </div>
</SidePanel>

<style lang="scss">
    @use '../../../../../styles/music-classical/core/mixins/balance-line-break'
        as *;

    .up-next-queue {
        padding-inline-start: 10px;
        padding-inline-end: 10px;
        padding-bottom: 30px;
        min-height: 100%;

        // remove border from queue items that follow a work header
        :global(.header + .queue-list .up-next-item:first-of-type::before) {
            --up-next-item-divider-style: none;
        }

        :global(.title__button) {
            @include balance-line-break;
        }
    }

    .up-next-queue__button {
        position: relative;
        width: 32px;
        height: 28px;
        border-radius: 4px;
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: var(--z-default);

        &.active {
            &:not(:disabled) {
                background: var(--playerPlatterButtonBGFill);

                :global(svg) {
                    fill: var(--playerPlatterButtonIconFill);
                }
            }
        }

        :global(svg) {
            fill: var(--systemSecondary);
        }
    }

    .up-next-queue__empty {
        height: 100%;
        width: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
        text-align: center;
        padding-inline-end: var(--side-panel-horizontal-padding, 0);
        padding-inline-start: var(--side-panel-horizontal-padding, 0);
    }

    .side-panel-controls__clear {
        color: var(--keyColor);
        font: var(--title-3);
        padding-inline-start: 10px;
        padding-inline-end: 10px;
    }
</style>
